import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../components';
import { ArrowLeft } from 'lucide-react';

type LegalItem = string | { label: string; text: string };
interface LegalSection {
  title: string;
  paragraphs?: string[];
  items?: LegalItem[];
  subsections?: LegalSection[];
}

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const renderItems = (items?: LegalItem[]) =>
  items && items.length > 0 ? (
    <ul>
      {items.map((item, i) =>
        typeof item === 'string' ? (
          <li key={i}>{item}</li>
        ) : (
          <li key={i}><strong>{item.label}</strong> {item.text}</li>
        )
      )}
    </ul>
  ) : null;

export const PrivacyPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const intro = asArray<string>(t('privacy.intro', { returnObjects: true }));
  const sections = asArray<LegalSection>(t('privacy.sections', { returnObjects: true }));
  const contactLines = asArray<string>(t('privacy.contactLines', { returnObjects: true }));

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/register" className="inline-flex items-center text-blue-600 hover:text-blue-500 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('privacy.backToRegistration')}
        </Link>

        <Card>
          <div className="prose prose-blue max-w-none">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('privacy.title')}</h1>

            <p className="text-sm text-gray-600 mb-6">{t('privacy.version')}</p>

            {intro.map((p, i) => <p key={i}>{p}</p>)}

            {sections.map((section, i) => (
              <React.Fragment key={i}>
                <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">{section.title}</h2>
                {section.paragraphs?.map((p, j) => <p key={j}>{p}</p>)}
                {renderItems(section.items)}
                {section.subsections?.map((sub, j) => (
                  <React.Fragment key={j}>
                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">{sub.title}</h3>
                    {sub.paragraphs?.map((p, k) => <p key={k}>{p}</p>)}
                    {renderItems(sub.items)}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}

            <p>
              {t('privacy.termsRef')} <Link to="/terms" className="text-blue-600 hover:text-blue-500">{t('privacy.termsLink')}</Link>.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">{t('privacy.contactTitle')}</h2>
            <p>
              {contactLines.map((line, i) => (
                <React.Fragment key={i}>
                  {i === 0 ? <strong>{line}</strong> : line}
                  {i < contactLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
